import React, { FC } from "react";
import SunEditor from "suneditor-react";
import { SunEditorReactProps } from "suneditor-react/dist/types/SunEditorReactProps";
import { video as sunEditorVideoPlugin } from "suneditor/src/plugins";
import { richTextTemplate } from "./rich-text-template";
import {
  isMicrosoftWordHtml,
  preserveInlinePasteWhitespace,
} from "./preserve-paste-whitespace";
import { removeLineBreaks } from "../../helpers";
import s from "./app-rich-text.module.scss";

const patchSunEditorVideoPlugin = () => {
  const plugin = sunEditorVideoPlugin as any;
  const patchKey = "__fnlVkIframePatch";

  if (plugin[patchKey]) {
    return;
  }

  const originalAdd = plugin.add;
  const originalSelect = plugin.select;
  const originalCreateVideo = plugin.create_video;
  const originalSetupUrl = plugin.setup_url;
  const iframeVideoUrlRegExp = /embed|iframe|player|\/e\/|\.php|\.html?/i;

  plugin.add = function (core: any, targetElement: any) {
    const result = originalAdd.call(this, core, targetElement);
    if (!isVkVideoOnlyOptions(core.options)) {
      return result;
    }

    const urlInput = core.context.video?.videoUrlFile;
    const urlLabel = urlInput
      ?.closest(".se-dialog-form")
      ?.querySelector("label");
    const modalTitle =
      core.context.video?.modal?.querySelector(".se-modal-title");

    if (modalTitle) {
      modalTitle.textContent = "Добавить VK видео";
    }

    if (urlLabel) {
      urlLabel.textContent = "Ссылка на VK видео";
    }

    if (urlInput) {
      urlInput.setAttribute(
        "placeholder",
        "https://vk.com/video_ext.php?oid=...&id=..."
      );
    }

    return result;
  };

  plugin.select = function (element: HTMLElement) {
    const result = originalSelect.call(this, element);

    if (isVkVideoOnlyOptions(this.options)) {
      this.context.resizing.resizeDisplay.style.display = "none";
    }

    return result;
  };

  plugin.setup_url = function (url: string) {
    if (!isVkVideoOnlyOptions(this.options)) {
      return originalSetupUrl.call(this, url);
    }

    const videoUrl = getVkVideoSrc(url || this.context.video?._linkValue);

    if (!videoUrl) {
      this.closeLoading();
      this.functions.noticeOpen(
        "Можно добавить только ссылку на VK видео формата https://vk.com/video_ext.php?... или https://vk.ru/video_ext.php?..."
      );
      return false;
    }

    return originalSetupUrl.call(this, videoUrl);
  };

  plugin.create_video = function (
    oFrame: any,
    src: string,
    width: string,
    height: string,
    align: string,
    file: any,
    isUpdate: boolean
  ) {
    const contextVideo = this.context.video;
    const currentElement = contextVideo?._element;

    if (isUpdate && currentElement && iframeVideoUrlRegExp.test(src)) {
      if (/^iframe$/i.test(currentElement.nodeName)) {
        currentElement.src = src;
      } else {
        const iframe = this.plugins.video.createIframeTag.call(this);
        iframe.src = src;
        currentElement.parentNode.replaceChild(iframe, currentElement);
        contextVideo._element = iframe;
      }
    }

    const result = originalCreateVideo.call(
      this,
      oFrame,
      src,
      width,
      height,
      align,
      file,
      isUpdate
    );

    if (isVkVideoOnlyOptions(this.options)) {
      applyResponsiveVideoStyles(
        this.context.video?._container,
        this.context.video?._element
      );
    }

    return result;
  };

  plugin[patchKey] = true;
};

const videoWrapperStyle =
  "position: relative; width: 100%; height: 0; padding-bottom: 56.25%; overflow: hidden;";
const videoIframeStyle =
  "position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;";

const isVkVideoSrc = (src: string) =>
  /^https:\/\/(vk\.com|vk\.ru)\/video_ext\.php\?/i.test(src.trim());

const isVkVideoOnlyOptions = (options: any) =>
  options?.videoFileInput === false &&
  options?.videoResizing === false &&
  options?.videoAlignShow === false;

const getVkVideoSrc = (value: string) => {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return "";
  }

  if (/^<iframe.*\/iframe>$/i.test(trimmedValue)) {
    const iframe = new DOMParser()
      .parseFromString(trimmedValue, "text/html")
      .querySelector("iframe");
    const iframeSrc = iframe?.getAttribute("src") || "";

    return isVkVideoSrc(iframeSrc) ? iframeSrc : "";
  }

  return isVkVideoSrc(trimmedValue) ? trimmedValue : "";
};

const applyResponsiveVideoStyles = (
  container: HTMLElement | null,
  iframe: HTMLElement | null
) => {
  const figure = container?.querySelector<HTMLElement>("figure");

  if (container) {
    container.setAttribute("style", "width: 100%; max-width: 100%;");
  }

  if (figure) {
    figure.setAttribute("style", videoWrapperStyle);
  }

  if (iframe) {
    iframe.setAttribute("style", videoIframeStyle);
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "true");

    if (!iframe.getAttribute("allow")) {
      iframe.setAttribute(
        "allow",
        "autoplay; encrypted-media; fullscreen; picture-in-picture;"
      );
    }
  }
};

const normalizeVideoEmbeds = (html: string) => {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = html;

  tempElement
    .querySelectorAll<HTMLIFrameElement>(".se-video-container iframe")
    .forEach((iframe) => {
      const wrapper = document.createElement("div");
      const nextIframe = document.createElement("iframe");
      const container = iframe.closest(".se-video-container");

      if (!container) {
        return;
      }

      wrapper.className = "fnl-news-video";
      wrapper.setAttribute("style", videoWrapperStyle);

      Array.from(iframe.attributes).forEach((attribute) => {
        nextIframe.setAttribute(attribute.name, attribute.value);
      });
      nextIframe.setAttribute("style", videoIframeStyle);
      nextIframe.setAttribute("frameborder", "0");
      nextIframe.setAttribute("allowfullscreen", "true");

      if (!nextIframe.getAttribute("allow")) {
        nextIframe.setAttribute(
          "allow",
          "autoplay; encrypted-media; fullscreen; picture-in-picture;"
        );
      }

      wrapper.appendChild(nextIframe);
      container.replaceWith(wrapper);
    });

  return tempElement.innerHTML;
};

patchSunEditorVideoPlugin();

interface IAppRichTextProps {
  richTextProps?: SunEditorReactProps;
  editorTemplate?: string[][];
  readonly?: boolean;
  stickyToolbar?: string | number | undefined;
}

export const AppRichText: FC<IAppRichTextProps> = ({
  richTextProps,
  editorTemplate,
  readonly,
  ...props
}) => {
  const { value, onChange } = props as any;
  const { setOptions: richTextSetOptions, ...restRichTextProps } =
    richTextProps || {};
  const needNormalizeVideoEmbeds = isVkVideoOnlyOptions(richTextSetOptions);

  const validateHTMLTags = (data: string) => {
    const html = needNormalizeVideoEmbeds
      ? normalizeVideoEmbeds(removeLineBreaks(data))
      : removeLineBreaks(data);
    const regExp = /<([^>\s]+)[^>]*>.*?<\/\1>|<[^/]+\/>/g;
    const matches = html.match(regExp);

    if (matches && matches.length > 0) {
      for (let i = 0; i < matches.length; i++) {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = matches[i];
        const textContent =
          tempElement.textContent && tempElement.textContent.trim();
        const mediaContent = tempElement.querySelector(
          "img, iframe, video, audio"
        );
        if ((textContent && textContent.length > 0) || mediaContent) {
          return html;
        }
      }
    }
    return undefined;
  };

  const handleEditorStateChange = (html: string) => {
    onChange(validateHTMLTags(html));
  };

  const handleEditorPaste = (
    event: ClipboardEvent,
    _cleanData: string,
    _maxCharCount: boolean,
    editorCore?: {
      cleanHTML: (
        html: string,
        whitelist?: RegExp,
        blacklist?: RegExp
      ) => string;
      pasteTagsWhitelistRegExp: RegExp;
      pasteTagsBlacklistRegExp: RegExp;
    }
  ): string | void => {
    const clipboardHtml = event.clipboardData?.getData("text/html");

    if (!clipboardHtml || !isMicrosoftWordHtml(clipboardHtml) || !editorCore) {
      return;
    }

    const preservedHtml = preserveInlinePasteWhitespace(clipboardHtml);

    if (preservedHtml === clipboardHtml) {
      return;
    }

    // SunEditor calls onPaste after its first cleanup. Return a freshly cleaned
    // version of the original Word HTML so the stripped separators are restored
    // while the library still applies its normal tag and attribute rules.
    return editorCore.cleanHTML(
      preservedHtml,
      editorCore.pasteTagsWhitelistRegExp,
      editorCore.pasteTagsBlacklistRegExp
    );
  };

  return (
    <SunEditor
      defaultValue={value}
      setContents={value || ""}
      disable={readonly}
      onChange={handleEditorStateChange}
      onPaste={handleEditorPaste}
      height={"400"}
      lang={"ru"}
      setOptions={{
        className: s.richText,
        buttonList: editorTemplate || richTextTemplate.default,
        imageFileInput: true,
        ...richTextSetOptions,
      }}
      {...restRichTextProps}
    />
  );
};
